import { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_03, SETTING_API_09 } from "../../api/ApiModel.tsx";
import { externalgetApi } from "../../services/externalApis.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import { externalpostApi } from "../../services/externalApis.tsx";
import { useLocation } from "react-router-dom";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Divider } from "@mui/material";
// Import messages
import { 
  Messages,
  MessageCategory,
 } from "../../utils/sys/Messages.tsx";

// Define interfaces
interface DocumentType {
  doctypecode: number;
  doctype: string;
  descrip?: string;
  doccatcode: number;
  docsavecode: string;
  [key: string]: any;
}

interface DocumentCategory {
  doccatcode: number;
  doccatname: string;
  [key: string]: any;
}

interface UserParams {
  LOCCODE: string;
  STAFFMAPLOCODE: string;
  RESTCODE: string;
  USERNAME: string;
  COMPCODE: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: DocumentType;
  actionText?: string;
  selectedDoccatcode?: number;
  selectedDoccatname?: string;
}

interface SelectedItemDetails {
  doctype: string;
  descrip: string;
  doccatcode: string;
  docsavecode: string;
  doctypecode?: number;
}

interface ErrorState {
  doctype?: string;
  docsavecode?: string;
  doccatname?: string;
  [key: string]: string | undefined;
}

interface ApiResponse<T> {
  data?: T;
  [key: string]: any;
}

interface RequiredField {
  name: string;
  value: any;
  label: string;
}

function CreateDocumentTypes(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const [data, setData] = useState<DocumentType[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { selectedItem } = locationState || {};
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "info" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");
  
  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, USERNAME, COMPCODE } =
    (SYS_USER.USER_PARAM as UserParams) || {};
  
  const isEdit = locationState?.actionText === "Edit";
  const { selectedDoccatcode, selectedDoccatname } = locationState || {};
  
  const [datadoccat, setDatadoccat] = useState<DocumentCategory[] | null>(null);
  const [selectedItemDetails, setSelectedItemDetails] = useState<SelectedItemDetails>({
    doctype: "",
    descrip: "",
    doccatcode: "",
    docsavecode: "",
  });

  const [items, setItems] = useState<DocumentType[]>([]);

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        doctype: selectedItem.doctype,
        doccatcode: selectedDoccatcode?.toString() || "",
        doctypecode: selectedItem.doctypecode,
        docsavecode: selectedItem.docsavecode,
        descrip: selectedItem.descrip || "",
      });
    }
  }, [selectedItem, selectedDoccatcode]);

  useEffect(() => {
    setPageData({
      title: "Document Types",
    });
  }, [setPageData]);

  const goBackToView = (): void => {
    navigate("/vcreatedocumenttypes");
  };

  const cleanInputValue = (name: string, value: string): string => {
    return value.replace(/^\s+/, ""); // remove leading spaces
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    // Auto-clean the input (remove leading spaces)
    const cleanedValue = cleanInputValue(name, value);
    const trimmedValue = cleanedValue.trim();

    // Prevent initial space only
    if (value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate max length
    if (cleanedValue.length > 30) {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: Messages.documentType.error.inputMaxLength,
      }));
      return;
    }

    // Validate empty or space-only input
    if (trimmedValue === "") {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: Messages.documentType.error.inputEmpty,
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }

    // Always update with the cleaned value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: cleanedValue,
    }));
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_09, "get", {
          STS: CON_STATUS_ACTIVE,
        });

        setDatadoccat(result as DocumentCategory[]);
        setIsLoading(false);
      } catch (err) {
        setError({ fetch: Messages.documentType.error.fetchCategoriesFailed });
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_03, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          doccatcode: selectedDoccatcode,
          STS: CON_STATUS_ACTIVE && "D",
        });

        setData(result as DocumentType[]);
        setIsLoading(false);
      } catch (err) {
        setError({ fetch: Messages.documentType.error.fetchTypesFailed });
        setIsLoading(false);
      }
    };

    if (selectedDoccatcode) {
      fetchData();
    }
  }, [selectedDoccatcode, COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const result = await externalgetApi(
          `${SETTING_API_03}/${selectedDoccatcode}`,
          "get",
          { doccatcode: selectedDoccatcode }
        );

        setItems(result as DocumentType[]);
      } catch (err) {
        setError({ fetch: Messages.documentType.error.fetchItemsFailed });
        console.error("Fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedDoccatcode) {
      fetchData();
    }
  }, [selectedDoccatcode]);

  const handleCancel = (): void => {
    setSelectedItemDetails({
      doctype: "",
      descrip: "",
      docsavecode: "",
      doccatcode: "",
    });
    setError({});
  };

  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);
    const { doctype, docsavecode } = selectedItemDetails;

    // Required field validation
    const requiredFields: RequiredField[] = [
      {
        name: "doccatname",
        value: selectedDoccatcode,
        label: Messages.documentType.warning.categoryRequired,
      },
      { name: "doctype", value: doctype, label: Messages.documentType.warning.nameRequired },
      { name: "docsavecode", value: docsavecode, label: Messages.documentType.warning.saveCodeRequired },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) => !field.value
    );
    if (missingRequiredFields.length > 0) {
      setError((prev) => ({
        ...prev,
        ...Object.fromEntries(
          missingRequiredFields.map((f) => [f.name, f.label])
        ),
      }));

      setAlertType("error");
      setAlertMessage(Messages.documentType.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Always fetch latest document list for the selected category
    let currentCategoryDocs: DocumentType[] = [];
    try {
      const result = await externalgetApi(SETTING_API_03, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        doccatcode: selectedDoccatcode,
        STS: CON_STATUS_ACTIVE,
      });
      currentCategoryDocs = Array.isArray(result) ? result : [];
    } catch (err) {
      console.error("Error fetching for duplicate check:", err);
    }

    // Proper duplicate detection (case-insensitive)
    const isDuplicate = currentCategoryDocs.some((item: DocumentType) => {
      const sameName =
        item.doctype?.trim().toLowerCase() === doctype?.trim().toLowerCase();
      const sameCategory =
        String(item.doccatcode) === String(selectedDoccatcode);

      const notSameRecord =
        !isEdit || item.doctypecode !== selectedItemDetails.doctypecode;

      return notSameRecord && sameCategory && sameName;
    });

    if (isDuplicate) {
      setAlertType("warning");
      setAlertMessage(Messages.documentType.warning.alreadyExists);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Prepare payload
    const DOCTYPE_CODE = selectedItem ? selectedItem.doctypecode : 0;

    interface Payload {
      doctype: string;
      descrip: string;
      docsavecode: string;
      doctypecode?: number;
      COMPCODE: string;
      LOCCODE: string;
      doccatcode: number;
      STS: string;
      ENUSER: string;
    }

    const payload: Payload = {
      doctype: selectedItemDetails.doctype,
      descrip: selectedItemDetails.descrip,
      docsavecode: selectedItemDetails.docsavecode,
      doctypecode: DOCTYPE_CODE,
      COMPCODE: COMPCODE || "",
      LOCCODE: STAFFMAPLOCODE || "",
      doccatcode: parseInt(selectedDoccatcode?.toString() || "0", 10),
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME || "",
    };

    try {
      const response = await externalpostApi(SETTING_API_03, payload);

      setAlertType("success");
      setAlertMessage(
        isEdit ? Messages.documentType.success.update : Messages.documentType.success.create
      );
      setAlertOpen(true);

      setSelectedItemDetails({
        doctype: "",
        descrip: "",
        docsavecode: "",
        doccatcode: "",
      });
    } catch (error) {
      console.error("Save error:", error);
      setAlertType("error");
      setAlertMessage(Messages.documentType.error.unexpected);
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const isDescripInList = items.some(
    (item: DocumentType) =>
      item.doctype?.trim().toLowerCase() ===
      selectedItemDetails.doctype?.trim().toLowerCase()
  );

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            component="button"
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
            component="button"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            component="button"
            aria-current="page"
          >
            Document Types
          </Link>
        </Breadcrumbs>
      </Box>
      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10, md: 10, lg: 10, xl: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? "Edit Document Type" : "Create Document Type"} :{" "}
                {selectedDoccatname}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={styles.add_form_page_container}
        >
          <Grid
            container
            spacing={2}
            sx={styles.add_form_page_grid_container}
            alignItems="stretch"
          >
            {/* Left Section */}
            <Grid
              size={{ xs: 12, sm: 6, md: 5, lg: 5, xl: 5 }}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography sx={styles.appbar_title}>Documents List</Typography>
              <List
                dense
                sx={{ p: 0, mt: 2, maxHeight: 400, overflow: "auto" }}
              >
                {items.map((item: DocumentType) => (
                  <ListItem
                    key={item.doctypecode}
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0px 1px 5px rgba(0,0,0,0.1)",
                      mb: 1,
                      bgcolor: "#fff",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemText
                      primary={item.doctype}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#333",
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        id="btn_add"
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{
                          bgcolor: "#1976d2",
                          "&:hover": { bgcolor: "#115293" },
                          textTransform: "none",
                        }}
                        onClick={() =>
                          setSelectedItemDetails((prevDetails) => ({
                            ...prevDetails,
                            doctype: item.doctype,
                            docsavecode: item.docsavecode,
                          }))
                        }
                      >
                        Add
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            {/* Right Section */}
            <Grid
              size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TextField
                id="txt_doccatname"
                label="Document Category"
                variant="outlined"
                fullWidth
                required
                size="small"
                name="doccatname"
                value={selectedDoccatname || ""}
                disabled
                sx={{ mt: 2 }}
                error={!!error.doccatname}
                helperText={error.doccatname}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />

              <TextField
                id="txt_doctype"
                label="Document Name"
                variant="outlined"
                fullWidth
                required
                size="small"
                name="doctype"
                value={selectedItemDetails.doctype}
                onChange={handleInputChange}
                error={!!error.doctype}
                sx={{ mt: 2 }}
                helperText={error.doctype}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
                disabled={isDescripInList}
              />

              <TextField
                id="txt_docsavecode"
                label="Save Code"
                variant="outlined"
                fullWidth
                required
                size="small"
                name="docsavecode"
                value={selectedItemDetails.docsavecode}
                onChange={handleInputChange}
                error={!!error.docsavecode}
                sx={{ mt: 2 }}
                helperText={error.docsavecode}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
                disabled={isDescripInList}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_clear"
            variant="outlined"
            size="medium"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
            disabled={isButtonDisabled}
          >
            Clear
          </Button>
          <Button
            id="btn_confirm/modify"
            variant="contained"
            size="medium"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            onClick={handleConfirmClick}
            disabled={isButtonDisabled}
          >
            {isEdit ? "Modify" : "Confirm"}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateDocumentTypes;